import React from 'react';
import {
    CTable,
    CTableHead,
    CTableBody,
    CTableRow,
    CTableHeaderCell,
    CTableDataCell,
    CFormInput,
    CFormSelect,
    CFormCheck,
    CButton,
    CTooltip,
} from '@coreui/react';
import type { ColumnDef, FilterDef, SortState, FilterState } from './types.js';
import { MultiSelectFilter } from './MultiSelectFilter.js';

interface DataTableProps<T> {
    data: T[];
    columns: ColumnDef<T>[];
    filters: FilterDef<T>[];
    sortState: SortState;
    filterState: FilterState;
    onSort: (key: string) => void;
    onFilter: (key: string, value: string) => void;
    onClearFilters?: () => void;
    selectedRows: Set<string>;
    onToggleSelect: (symbol: string, checked: boolean) => void;
    onToggleSelectAll: (checked: boolean) => void;
    /** Row expansion is only meaningful alongside `renderDetail`; omit both for a flat table. */
    expandedRow?: string | null;
    onToggleExpand?: (symbol: string) => void;
    renderDetail?: (row: T) => React.ReactNode;
    getRowKey: (row: T) => string;
    /**
     * Per-row styling, e.g. tinting a row by its risk decision. A selected row's highlight
     * still wins over whatever this returns.
     */
    rowStyle?: (row: T) => React.CSSProperties | undefined;
    /** Groups (by ColumnDef.group) currently collapsed to a single placeholder column. */
    collapsedGroups?: Set<string>;
    onToggleGroup?: (group: string) => void;
}

/**
 * A synthetic, non-data column standing in for an entire collapsed group. Inherits `sticky`
 * from the group's first member (`firstCol`) so a pinned-left group (e.g. the combined
 * Upside % + Cup & Handle status block) collapses down to one still-pinned column, rather
 * than breaking the sticky block's contiguity by dropping out of the sticky flow.
 *
 * Does NOT inherit `firstCol.width` as-is: a sticky column's width is sized for its own short
 * cell content (e.g. Upside %'s 90px), which is usually narrower than the group's label text
 * (e.g. "Cup & Handle"). Sticky cells enforce `maxWidth` with no wrapping, so content wider
 * than that clips visually into whatever renders after it -- the reversal columns, once
 * they've shifted left to fill the space this collapse just freed up. `MIN_LABEL_WIDTH` gives
 * the label room to render in full instead.
 */
const MIN_LABEL_WIDTH = 130;

function collapsedGroupColumn<T>(group: string, firstCol: ColumnDef<T>): ColumnDef<T> {
    return {
        key: `__collapsed_${group}`,
        label: firstCol.groupLabel ?? group,
        accessor: () => null,
        type: 'text',
        sortable: false,
        filterable: false,
        headerColor: firstCol.headerColor,
        sticky: firstCol.sticky,
        width: firstCol.sticky ? Math.max(firstCol.width ?? 140, MIN_LABEL_WIDTH) : firstCol.width,
    };
}

/**
 * Collapses every column sharing a group in `collapsedGroups` down to one placeholder column
 * (keyed by group, positioned where the group's first column would have been); all other
 * columns pass through unchanged. Columns with no `group` are never affected.
 */
function applyGroupCollapse<T>(
    columns: ColumnDef<T>[],
    collapsedGroups: Set<string>,
): ColumnDef<T>[] {
    const seenGroups = new Set<string>();
    const result: ColumnDef<T>[] = [];
    for (const col of columns) {
        if (!col.group || !collapsedGroups.has(col.group)) {
            result.push(col);
            continue;
        }
        if (seenGroups.has(col.group)) continue;
        seenGroups.add(col.group);
        result.push(collapsedGroupColumn(col.group, col));
    }
    return result;
}

function DataTable<T>({
    data,
    columns: allColumns,
    filters,
    sortState,
    filterState,
    onSort,
    onFilter,
    onClearFilters,
    selectedRows,
    onToggleSelect,
    onToggleSelectAll,
    expandedRow = null,
    onToggleExpand,
    renderDetail,
    getRowKey,
    rowStyle,
    collapsedGroups = new Set(),
    onToggleGroup,
}: DataTableProps<T>) {
    const allSelected = data.length > 0 && data.every((row) => selectedRows.has(getRowKey(row)));
    const someSelected = data.some((row) => selectedRows.has(getRowKey(row)));
    const hasActiveFilters = Object.values(filterState).some((v) => v !== '');

    const columns = applyGroupCollapse(allColumns, collapsedGroups);

    // The first column (in original, uncollapsed order) carrying a given group gets the
    // collapse/expand toggle rendered in its header -- this stays stable regardless of which
    // groups are currently collapsed, so the toggle doesn't jump to a different column.
    const groupToggleKey = new Map<string, string>();
    const groupLabelByGroup = new Map<string, string>();
    for (const col of allColumns) {
        if (!col.group) continue;
        if (!groupToggleKey.has(col.group)) groupToggleKey.set(col.group, col.key);
        if (!groupLabelByGroup.has(col.group)) {
            groupLabelByGroup.set(col.group, col.groupLabel ?? col.group);
        }
    }
    // A collapsed group's placeholder column (key `__collapsed_<group>`) always carries the
    // toggle too, since it's the sole visible stand-in for that group.
    for (const group of collapsedGroups) {
        groupToggleKey.set(group, `__collapsed_${group}`);
    }
    const toggleForColumnKey = new Map(
        [...groupToggleKey.entries()].map(([group, key]) => [key, group]),
    );

    const filterMap = Object.fromEntries(filters.map((f) => [f.key, f]));

    // The select checkbox sits at a fixed 36px column before the data columns, so sticky
    // data columns need their left-offset computed cumulative to that, in column order.
    const CHECKBOX_COL_WIDTH = 36;
    const stickyLeftByKey = new Map<string, number>();
    let stickyOffset = CHECKBOX_COL_WIDTH;
    for (const col of columns) {
        if (!col.sticky) continue;
        stickyLeftByKey.set(col.key, stickyOffset);
        stickyOffset += col.width ?? 140;
    }

    const DEFAULT_CELL_BG = 'var(--card)';

    /**
     * CoreUI's `striped` prop (below, on `<CTable>`) zebra-stripes odd rows via an opaque
     * `box-shadow: inset 0 0 0 9999px var(--cui-table-bg-type)` on every `<td>`
     * (`.table > :not(caption) > * > *`), which paints ON TOP of background-color/
     * background-image. That silently masks any non-default cell background on odd rows,
     * regardless of the background's own opacity -- even though the background itself
     * correctly wins the background-color/background-image cascade against CoreUI's class
     * rule, so `getComputedStyle` still reports it correctly. Only the paint is wrong.
     *
     * Route every per-cell background through `stickyStyle`'s `background` param (or through
     * this helper directly, as the checkbox cell does) rather than setting `background`/
     * `backgroundColor` on a `<CTableDataCell>` some other way -- that's what keeps this
     * override automatic for any future tint/highlight, instead of relying on each call site
     * remembering to add it.
     */
    const stripeOverrideFor = (background: string): React.CSSProperties | undefined =>
        background !== DEFAULT_CELL_BG
            ? ({ '--cui-table-bg-type': 'transparent' } as React.CSSProperties)
            : undefined;

    const stickyStyle = (
        col: ColumnDef<T>,
        isHeader: boolean,
        background = DEFAULT_CELL_BG,
    ): React.CSSProperties | undefined => {
        if (!col.sticky) return stripeOverrideFor(background);
        return {
            position: 'sticky',
            left: stickyLeftByKey.get(col.key),
            width: col.width,
            minWidth: col.width,
            maxWidth: col.width,
            // Content wider than maxWidth would otherwise render past the cell's boundary and
            // visually bleed into whatever sits to its right -- a real bug this surfaced: the
            // collapsed group placeholder's label overflowed a too-narrow inherited width and
            // overlapped the Reversal columns once they shifted left to fill the freed space.
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            zIndex: isHeader ? 3 : 1,
            background,
            ...stripeOverrideFor(background),
        };
    };

    function renderCell(col: ColumnDef<T>, row: T): React.ReactNode {
        const raw = col.accessor(row);
        if (col.render) return col.render(raw, row);
        if (raw == null) return '—';
        if (col.formatter) return col.formatter(raw);
        return String(raw);
    }

    function sortArrow(key: string): string {
        if (sortState.key !== key) return '';
        return sortState.direction === 'asc' ? ' ▲' : ' ▼';
    }

    return (
        <div className="table-scroll-container">
            <CTable hover striped small>
                <CTableHead className="sticky-top" style={{ zIndex: 2, background: 'var(--card)' }}>
                    <CTableRow>
                        <CTableHeaderCell
                            style={{
                                width: CHECKBOX_COL_WIDTH,
                                position: 'sticky',
                                left: 0,
                                zIndex: 3,
                                background: 'var(--card)',
                            }}
                        >
                            <CFormCheck
                                checked={allSelected}
                                indeterminate={!allSelected && someSelected}
                                onChange={(e) => onToggleSelectAll(e.target.checked)}
                            />
                        </CTableHeaderCell>
                        {columns.map((col) => {
                            const toggleGroup = toggleForColumnKey.get(col.key);
                            return (
                                <CTableHeaderCell
                                    key={col.key}
                                    onClick={
                                        col.sortable !== false ? () => onSort(col.key) : undefined
                                    }
                                    style={{
                                        ...(col.sortable !== false
                                            ? { cursor: 'pointer', userSelect: 'none' }
                                            : undefined),
                                        ...(col.headerColor
                                            ? { boxShadow: `inset 0 -3px 0 0 ${col.headerColor}` }
                                            : undefined),
                                        ...stickyStyle(col, true),
                                    }}
                                >
                                    {toggleGroup !== undefined && onToggleGroup && (
                                        <button
                                            type="button"
                                            aria-label={
                                                collapsedGroups.has(toggleGroup)
                                                    ? `Expand ${groupLabelByGroup.get(toggleGroup) ?? ''} columns`
                                                    : `Collapse ${groupLabelByGroup.get(toggleGroup) ?? ''} columns`
                                            }
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onToggleGroup(toggleGroup);
                                            }}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: '0 4px 0 0',
                                                fontSize: 11,
                                                opacity: 0.8,
                                            }}
                                        >
                                            {collapsedGroups.has(toggleGroup) ? '▶' : '◀'}
                                        </button>
                                    )}
                                    {col.description ? (
                                        <CTooltip content={col.description} placement="top">
                                            <span
                                                style={{
                                                    borderBottom: '1px dotted currentColor',
                                                    cursor:
                                                        col.sortable !== false ? 'pointer' : 'help',
                                                }}
                                            >
                                                {col.label}
                                            </span>
                                        </CTooltip>
                                    ) : (
                                        col.label
                                    )}
                                    {col.sortable !== false && (
                                        <span style={{ opacity: 0.6 }}>{sortArrow(col.key)}</span>
                                    )}
                                </CTableHeaderCell>
                            );
                        })}
                    </CTableRow>
                    <CTableRow>
                        <CTableHeaderCell
                            style={{
                                padding: '2px 4px',
                                verticalAlign: 'middle',
                                position: 'sticky',
                                left: 0,
                                zIndex: 3,
                                background: 'var(--card)',
                            }}
                        >
                            {hasActiveFilters && onClearFilters && (
                                <CButton
                                    color="danger"
                                    variant="ghost"
                                    size="sm"
                                    onClick={onClearFilters}
                                >
                                    Clear
                                </CButton>
                            )}
                        </CTableHeaderCell>
                        {columns.map((col) => {
                            const fd = filterMap[col.key];
                            return (
                                <CTableHeaderCell
                                    key={col.key}
                                    style={{ padding: '2px 4px', ...stickyStyle(col, true) }}
                                >
                                    {fd && col.filterable !== false ? (
                                        fd.type === 'select' ? (
                                            <CFormSelect
                                                size="sm"
                                                value={filterState[col.key] ?? ''}
                                                onChange={(e) => onFilter(col.key, e.target.value)}
                                            >
                                                <option value="">{fd.placeholder}</option>
                                                {fd.options?.map((opt) => (
                                                    <option key={opt} value={opt}>
                                                        {opt}
                                                    </option>
                                                ))}
                                            </CFormSelect>
                                        ) : fd.type === 'multiselect' ? (
                                            <MultiSelectFilter
                                                options={fd.options ?? []}
                                                selected={
                                                    filterState[col.key]
                                                        ? filterState[col.key].split(',')
                                                        : []
                                                }
                                                placeholder={fd.placeholder}
                                                onChange={(selected) =>
                                                    onFilter(col.key, selected.join(','))
                                                }
                                            />
                                        ) : (
                                            <CFormInput
                                                size="sm"
                                                placeholder={fd.placeholder}
                                                value={filterState[col.key] ?? ''}
                                                onChange={(e) => onFilter(col.key, e.target.value)}
                                            />
                                        )
                                    ) : null}
                                </CTableHeaderCell>
                            );
                        })}
                    </CTableRow>
                </CTableHead>
                <CTableBody>
                    {data.map((row) => {
                        const key = getRowKey(row);
                        const isSelected = selectedRows.has(key);
                        const isExpanded = expandedRow === key;
                        // Sticky cells paint their own opaque background (necessary so scrolled-
                        // under non-sticky columns don't show through) which otherwise fully
                        // occludes rowStyle's tint on every sticky column. Composite it under
                        // `var(--card)` via a two-layer background so sticky cells stay opaque
                        // AND show the tint, instead of just falling back to a plain card
                        // background. isSelected still wins outright, per this prop's contract.
                        const rowBackgroundColor = rowStyle?.(row)?.backgroundColor;
                        const stickyCellBg = isSelected
                            ? 'var(--cui-table-active-bg, rgba(0,0,0,.075))'
                            : rowBackgroundColor
                              ? `linear-gradient(${rowBackgroundColor}, ${rowBackgroundColor}), var(--card)`
                              : DEFAULT_CELL_BG;
                        return (
                            <React.Fragment key={key}>
                                <CTableRow
                                    onClick={onToggleExpand ? () => onToggleExpand(key) : undefined}
                                    style={{
                                        cursor: onToggleExpand ? 'pointer' : undefined,
                                        ...rowStyle?.(row),
                                        ...(isSelected
                                            ? {
                                                  backgroundColor:
                                                      'var(--cui-table-active-bg, rgba(0,0,0,.075))',
                                              }
                                            : {}),
                                    }}
                                >
                                    <CTableDataCell
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                            width: CHECKBOX_COL_WIDTH,
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 1,
                                            background: stickyCellBg,
                                            ...stripeOverrideFor(stickyCellBg),
                                        }}
                                    >
                                        <CFormCheck
                                            checked={isSelected}
                                            onChange={(e) => onToggleSelect(key, e.target.checked)}
                                        />
                                    </CTableDataCell>
                                    {columns.map((col) => (
                                        <CTableDataCell
                                            key={col.key}
                                            style={stickyStyle(col, false, stickyCellBg)}
                                        >
                                            {renderCell(col, row)}
                                        </CTableDataCell>
                                    ))}
                                </CTableRow>
                                {isExpanded && renderDetail && (
                                    <CTableRow>
                                        <CTableDataCell
                                            colSpan={columns.length + 1}
                                            style={{
                                                padding: '12px 16px',
                                                background: 'var(--card)',
                                            }}
                                        >
                                            {renderDetail(row)}
                                        </CTableDataCell>
                                    </CTableRow>
                                )}
                            </React.Fragment>
                        );
                    })}
                </CTableBody>
            </CTable>
        </div>
    );
}

export default DataTable;
